import classNames from 'classnames';
import React, {
  CSSProperties,
  ForwardedRef,
  forwardRef,
  MouseEventHandler,
  ReactNode,
} from 'react';

import styles from './Button.module.css';
import { Size } from '../../types';
import { getLinkRel, isExternalURL } from '../../utils';

export type ButtonType = 'button' | 'submit' | 'reset';

export type ButtonCategory = 'default' | 'danger' | 'neutral' | 'success';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'plain';

export type ButtonSize = Extract<Size, 'S' | 'M'>;

export type HTMLInteractiveElement = HTMLButtonElement | HTMLAnchorElement;

export interface ButtonProps {
  /**
   * Aria label for buttons accessiblity
   */
  ariaLabel?: string;
  /**
   * The category of a button
   */
  category: ButtonCategory;
  /**
   * The contents inside of the button element.
   * Should preferably contain just text or similar inline elements,
   * no block elements. Can be omitted to show only the `icon`.
   */
  children?: ReactNode;
  /**
   * Optional class name to add to the container element.
   * Do not alter the look of the component! Should be used only for sizing and positioning.
   */
  className?: string;
  /**
   * Needed to dertermine if field is disabled or not.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * Whether or not the link is considered external.
   * If undefined, this will be determined automatically from `href`.
   * Only has effect if `href` is defined.
   */
  external?: boolean;
  /**
   * To make the button to act as a valid accessible link, you may provide `href`,
   * as you would to a <a> tag.
   */
  href?: string;
  /**
   * The icon to be shown in the button.
   * It should preferably be a JSX element of an icon component imported
   * from `@sapappgyver/appgyver-design-system-icons`.
   */
  icon?: ReactNode;
  /**
   * The position of the icon.
   * Defaults to `left`.
   *
   * @default "left"
   */
  iconPosition?: 'left' | 'right' | undefined;
  /**
   * Click event handler for the button.
   */
  onClick?: MouseEventHandler<HTMLInteractiveElement>;
  /**
   * Mouse down event handler for the button.
   */
  onMouseDown?: MouseEventHandler<HTMLInteractiveElement>;
  /**
   * Mouse up event handler for the button.
   */
  onMouseUp?: MouseEventHandler<HTMLInteractiveElement>;
  /**
   * Whether or not to open the link in a new tab or window.
   * This only has effect if `href` is defined.
   */
  openInNewTab?: boolean;
  /**
   * The size of the button.
   *
   * @default "M"
   */
  size?: ButtonSize;
  /**
   * Optional inline styles to add to the container element.
   * Do not alter the look of the component! Should be used only for sizing and positioning.
   */
  style?: CSSProperties;
  /**
   * The type of a button component.
   * Note that type defaults to `button` (instead of `submit`) which is more convenient for most cases.
   * Only has effect if `href` is not defined.
   *
   * @default "button"
   */
  type?: ButtonType;
  /**
   * The variant of a button
   */
  variant: ButtonVariant;
}

const classNamesByCategory: Record<ButtonCategory, string> = {
  default: styles.default,
  danger: styles.danger,
  neutral: styles.neutral,
  success: styles.success,
};

const classNamesByVariant: Record<ButtonVariant, string> = {
  primary: styles.primary,
  secondary: styles.secondary,
  tertiary: styles.tertiary,
  plain: styles.plain,
};

/* eslint-disable @typescript-eslint/naming-convention */
const classNamesBySize: Record<ButtonSize, string> = {
  S: styles['size-s'],
  M: styles['size-m'],
};
/* eslint-enable @typescript-eslint/naming-convention */

export const Button = forwardRef<HTMLInteractiveElement, ButtonProps>(
  (
    {
      ariaLabel,
      category,
      children,
      className,
      disabled,
      href,
      external = href ? isExternalURL(href) : false,
      icon,
      iconPosition = 'left',
      onClick,
      onMouseDown,
      onMouseUp,
      openInNewTab = external,
      size = 'M',
      style,
      type = 'button',
      variant,
    },
    ref,
  ) => {
    const buttonClassName = classNames(
      className,
      styles.button,
      variant !== 'plain' && classNamesBySize[size],
      classNamesByCategory[category],
      classNamesByVariant[variant],
      disabled ? styles.disabled : styles.enabled,
      !!children && variant !== 'plain' && styles['horizontal-padding'],
    );
    const contents = (
      <>
        {icon && (
          <span
            className={classNames(
              styles.icon,
              iconPosition === 'right' && styles['icon-right'],
            )}
          >
            {icon}
          </span>
        )}
        {children}
      </>
    );
    if (href && !disabled) {
      /* Button is semantically a link. Render as an anchor tag */
      const rel = getLinkRel({ openInNewTab, isExternal: external });

      return (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a
          className={buttonClassName}
          href={href}
          onClick={disabled ? undefined : onClick}
          onMouseDown={disabled ? undefined : onMouseDown}
          onMouseUp={disabled ? undefined : onMouseUp}
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          rel={rel}
          style={style}
          target={openInNewTab ? '_blank' : undefined}
        >
          {contents}
        </a>
      );
    }

    /* Semantically a button */
    return (
      <button
        /* eslint-disable react/button-has-type */
        aria-label={ariaLabel}
        className={buttonClassName}
        disabled={disabled}
        onClick={disabled ? undefined : onClick}
        onMouseDown={disabled ? undefined : onMouseDown}
        onMouseUp={disabled ? undefined : onMouseUp}
        ref={ref as ForwardedRef<HTMLButtonElement>}
        style={style}
        type={type}
      >
        {contents}
      </button>
    );
  },
);
